import { useNewCode } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { ORGANIZATION_INVITATION_PERMISSIONS } from 'constants/organization_invitation_permissions'
import { NoSeatsRemainingModal, setAnalyticsLocation } from 'features/billing'
import {
  AddUsersModal,
  addUsersModalOpenSelector,
  openAddUserModal,
  setModalSuccessState,
} from 'features/invite_flow'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'
import { useTriggerSeatsPaywall } from 'hooks/useTriggerSeatsPaywall'
import {
  isScimConfiguredSelector,
  isSingleSelector,
  orgInvitationPermissionsSelector
} from 'selectors/current_organization'
import {
  isAdminOrOwnerSelector,
  isGroupAdminSelector
} from 'selectors/current_user'

import { ScimProTip } from './scim_pro_tip'

const AddNewUserButton = ({ url, onClick }) => (
  <Button
    as={Link}
    to={url}
    decoration="primary"
    onClick={onClick}
  >
    {I18n.t('publisher.admin.add_users')}
  </Button>
)

export const AddNewUser = () => {
  const dispatch = useDispatch()
  const track = useTrack()
  const { pathname } = useLocation()
  const inSettingsModal = useIsSettingsModalLayout()
  const isActivationOnboardingExperimentEnabled = useNewCode('activation_q3')?.enabled
  const totalSeatsRef = useRef()
  const [noSeatsRemainingModalOpen, setNoSeatsRemainingModalOpen] = useState(false)

  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector)
  const isGroupAdmin = useSelector(isGroupAdminSelector)
  const isScimConfigured = useSelector(isScimConfiguredSelector)
  const isSingleOrganization = useSelector(isSingleSelector)
  const orgInvitationPermissions = useSelector(orgInvitationPermissionsSelector)
  const isAddUsersModalOpen = useSelector(addUsersModalOpenSelector)

  const url = '/app/admin/invitations/new'

  const canInviteAsGroupAdmin = isGroupAdmin
    && [ORGANIZATION_INVITATION_PERMISSIONS.GROUP_ADMIN, ORGANIZATION_INVITATION_PERMISSIONS.USER].includes(orgInvitationPermissions)
  const canInviteAsEndUser = !isAdminOrOwner && !isGroupAdmin && orgInvitationPermissions === ORGANIZATION_INVITATION_PERMISSIONS.USER
  const canManageInvites = isAdminOrOwner || canInviteAsGroupAdmin || canInviteAsEndUser

  const addOnClick = () => {
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_key: 'admin_user',
        cta_text: 'invite_user',
        cta_destination: 'invite_teammate_flow'
      }
    })
  }

  const openInviteModal = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        modal_name: 'invite_user_email_flow',
        action: 'opening',
        path: pathname,
      },
    })
    dispatch(openAddUserModal())
  }, [dispatch, pathname, track])

  const [triggerPaywall, triggerState, resetTrigger] = useTriggerSeatsPaywall({
    onInviteOpen: openInviteModal,
    onNoSeatsOpen: () => setNoSeatsRemainingModalOpen(true),
    totalSeatsRef,
  })

  useEffect(() => {
    if (!inSettingsModal) return
    if (!triggerState || triggerState === 'invite-modal-open') return

    if (triggerState !== 'upgrade-modal-open') {
      track({
        name: TRACKING_EVENT_NAMES.PAYWALL_SHOWN,
        entities: ['attribution', 'organization', 'user'],
        properties: {
          paywall_feature: 'invite_flow_add_seats',
          paywall_type: 'popup',
          plan_required: 'standard',
        },
      })
    }
    resetTrigger()
  }, [inSettingsModal, resetTrigger, track, triggerState])

  const handleSettingsModalInviteClick = () => {
    addOnClick()
    dispatch(setAnalyticsLocation('people_settings_invite'))
    dispatch(setModalSuccessState(false))
    if (isActivationOnboardingExperimentEnabled) {
      triggerPaywall()
    } else {
      openInviteModal()
    }
  }

  return (
    <>
      {!isScimConfigured && canManageInvites && (
        inSettingsModal ? (
          <Button
            type="button"
            decoration="primary"
            onClick={handleSettingsModalInviteClick}
          >
            {I18n.t('publisher.admin.add_users')}
          </Button>
        ) : (
          <AddNewUserButton url={url} onClick={addOnClick} />
        )
      )}
      {inSettingsModal && (
        <>
          <AddUsersModal isOpen={isAddUsersModalOpen} />
          {isActivationOnboardingExperimentEnabled && (
            <NoSeatsRemainingModal
              isOpen={noSeatsRemainingModalOpen}
              onClose={() => setNoSeatsRemainingModalOpen(false)}
              totalSeats={totalSeatsRef.current}
            />
          )}
        </>
      )}
      {!isSingleOrganization && isScimConfigured && canManageInvites && <ScimProTip />}
    </>
  )
}
